import { type FC, useCallback, useEffect, useMemo } from 'react';

import { SaveSearchContainer, type TOnSubmitSaveSearchParams, useSaveSearchEvents } from '@/packages/SaveSearchModal';

import { useListingSelector } from '@/microfrontends/filters/shared/store/slices/listing/selectors';
import { useUserSelector } from '@/microfrontends/filters/shared/store/slices/user/selectors';

import { trackOpen } from './tracking';
import { usePushManager } from './usePushManager';

type TSaveSearchModalContainerProps = {
  isOpen: boolean;
  onSubscribed: (parameters: TOnSubmitSaveSearchParams) => void;
  onPushSubscribed: (subscribeToPushAvailable: boolean) => void;
  onOpen: () => void;
  onClose: () => void;
};

export const SaveSearchModalContainer: FC<TSaveSearchModalContainerProps> = ({
  isOpen,
  onSubscribed,
  onPushSubscribed,
  onOpen,
  onClose,
}) => {
  const listingState = useListingSelector();
  const defaultTitle = listingState.meta.h1;
  const user = useUserSelector();
  const defaultEmail = useMemo(() => (user.data.isAuthenticated ? user.data.email : null), [user]);
  const { subscribeToPushAvailable, handlePushSubscribe } = usePushManager(isOpen, trackOpen);

  useEffect(() => {
    onPushSubscribed(subscribeToPushAvailable);
  }, [subscribeToPushAvailable]);

  const handleSubscribed = useCallback(
    async ({ frequency, title, email, subscribeToNews, subscribeToPush }: TOnSubmitSaveSearchParams) => {
      if (onSubscribed) {
        onSubscribed({
          frequency,
          title,
          email,
          subscribeToNews,
          defaultTitle,
          defaultEmail,
          subscribeToPushAvailable,
          subscribeToPush,
        });
      }

      await handlePushSubscribe();

      if (window.__reloadHeader__) {
        window.__reloadHeader__();
      }
    },
    [defaultEmail, defaultTitle, subscribeToPushAvailable, onSubscribed, handlePushSubscribe],
  );

  useSaveSearchEvents(handleSubscribed, onOpen, onClose);

  return <SaveSearchContainer />;
};
